/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR GPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE files in the repository root for full details.
*/

import { isTauri } from "../utils/isTauri";

const GESTURE_KEYS = new Set(["+", "=", "-", "_", "0", "NumpadAdd", "NumpadSubtract", "Numpad0"]);
const EDGE_SWIPE_START_X = 32;
const SWIPE_DIRECTION_LOCK_DISTANCE = 10;
const MIN_SWIPE_DISTANCE = 64;
const MAX_SWIPE_VERTICAL_DELTA = 72;
const MAX_SWIPE_DURATION_MS = 600;

type SwipeEdge = "left" | "right";

interface SwipeState {
    touchId: number;
    startX: number;
    startY: number;
    startTime: number;
    edge: SwipeEdge;
    directionLocked: boolean;
}

let activeSwipe: SwipeState | null = null;
let gesturesInstalled = false;

function setViewportLock(): void {
    const viewport = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
    if (viewport) {
        const content = viewport.getAttribute("content") ?? "";
        const locked = `${content},maximum-scale=1,minimum-scale=1,user-scalable=no`;
        if (!/user-scalable=no|maximum-scale=1|minimum-scale=1/i.test(content)) {
            viewport.setAttribute("content", locked);
        }
    }

    document.body?.style.setProperty("touch-action", "pan-x pan-y");
    document.documentElement.style.setProperty("touch-action", "pan-x pan-y");
}

function onWheel(event: WheelEvent): void {
    if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
    }
}

function onTouchStart(event: TouchEvent): void {
    if (event.touches.length !== 1) {
        if (event.touches.length > 1) event.preventDefault();
        activeSwipe = null;
        return;
    }

    const touch = event.touches[0];
    if (!touch) {
        activeSwipe = null;
        return;
    }

    const edge = getSwipeEdge(touch);
    activeSwipe = edge
        ? {
              touchId: touch.identifier,
              startX: touch.clientX,
              startY: touch.clientY,
              startTime: performance.now(),
              edge,
              directionLocked: false,
          }
        : null;
}

function onTouchMove(event: TouchEvent): void {
    if (!activeSwipe) return;
    if (event.touches.length !== 1) {
        activeSwipe = null;
        return;
    }

    const touch = getTouch(event.touches, activeSwipe.touchId);
    if (!touch) {
        activeSwipe = null;
        return;
    }

    const deltaX = touch.clientX - activeSwipe.startX;
    const deltaY = touch.clientY - activeSwipe.startY;
    const directedDeltaX = getDirectedDeltaX(activeSwipe.edge, deltaX);

    if (activeSwipe.directionLocked) {
        if (directedDeltaX <= 0 || Math.abs(deltaY) > MAX_SWIPE_VERTICAL_DELTA) {
            activeSwipe = null;
            return;
        }
        event.preventDefault();
        return;
    }

    if (Math.max(Math.abs(directedDeltaX), Math.abs(deltaY)) < SWIPE_DIRECTION_LOCK_DISTANCE) return;

    // Let normal vertical scrolling win, and cancel a gesture that starts in the
    // wrong horizontal direction instead of re-arming it if the finger turns back.
    if (directedDeltaX <= 0 || Math.abs(deltaY) > directedDeltaX) {
        activeSwipe = null;
        return;
    }

    activeSwipe.directionLocked = true;
    event.preventDefault();
}

function onTouchEnd(event: TouchEvent): void {
    const swipe = activeSwipe;
    activeSwipe = null;
    if (!swipe) return;

    const touch = getTouch(event.changedTouches, swipe.touchId);
    if (!touch) return;

    const deltaX = touch.clientX - swipe.startX;
    const deltaY = touch.clientY - swipe.startY;
    const directedDeltaX = getDirectedDeltaX(swipe.edge, deltaX);
    const duration = performance.now() - swipe.startTime;

    if (
        swipe.directionLocked &&
        directedDeltaX >= MIN_SWIPE_DISTANCE &&
        duration <= MAX_SWIPE_DURATION_MS &&
        Math.abs(deltaY) <= MAX_SWIPE_VERTICAL_DELTA
    ) {
        if (window.history.length > 1) {
            event.preventDefault();
            window.history.back();
        }
    }
}

function onTouchCancel(): void {
    activeSwipe = null;
}

function getTouch(touches: TouchList, id: number): Touch | undefined {
    for (let i = 0; i < touches.length; i++) {
        const touch = touches[i];
        if (touch?.identifier === id) return touch;
    }
    return undefined;
}

function getSwipeEdge(touch: Touch): SwipeEdge | undefined {
    if (touch.clientX <= EDGE_SWIPE_START_X) return "left";

    const viewportWidth = document.documentElement.clientWidth || document.body?.clientWidth || 0;
    if (viewportWidth > EDGE_SWIPE_START_X * 2 && touch.clientX >= viewportWidth - EDGE_SWIPE_START_X) {
        return "right";
    }

    return undefined;
}

function getDirectedDeltaX(edge: SwipeEdge, deltaX: number): number {
    return edge === "left" ? deltaX : -deltaX;
}

function onKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && (GESTURE_KEYS.has(event.key) || GESTURE_KEYS.has(event.code))) {
        event.preventDefault();
    }
}

export function disablePinchToZoom(): void {
    if (!isTauri() || gesturesInstalled) return;
    gesturesInstalled = true;

    const opts = { passive: false };
    setViewportLock();

    window.addEventListener("wheel", onWheel, opts);
    window.addEventListener("touchstart", onTouchStart, opts);
    window.addEventListener("touchmove", onTouchMove, opts);
    window.addEventListener("touchend", onTouchEnd, opts);
    window.addEventListener("touchcancel", onTouchCancel, opts);
    window.addEventListener("keydown", onKeydown, opts);
}
